import { FC, useCallback } from 'react'
import tw from 'tailwind-styled-components'
import { ImageProps } from 'next/image'
import { useTranslation } from 'next-i18next'
import { Button } from './Button'
import { CloseButton } from './CloseButton'
import { ActiveButton } from './ActiveButton'
import { Popup, ChildrenProps } from './Popup'
import { useDeviceMediaQuery } from '../hooks'

type Props = {
  title: string
  icon?: ImageProps['src']
  onSave(): void
  onClear(): void
}

const Close = tw(CloseButton)`
  absolute
  top-3
  right-2
`
const Clear = tw.span`cursor-pointer text-primary-500 underline`

const Content: FC<Omit<Props, 'icon'> & ChildrenProps> = ({
  onSave,
  onClear,
  close,
  children,
  title,
}) => {
  const { mobile } = useDeviceMediaQuery()
  const { t } = useTranslation()
  const handleSave = useCallback(() => {
    onSave()
    close()
  }, [onSave, close])
  const handleClear = useCallback(() => {
    onClear()
    close()
  }, [onClear, close])

  return (
    <div className={mobile ? 'w-min' : 'w-[420px]'}>
      <Close onClick={close} />
      <div className='p-4'>
        <div className='text-sm mb-2'>{title}</div>
        {children}
      </div>
      <div className='p-4 border-t-2 flex items-center justify-between space-x-4'>
        <Clear onClick={handleClear}>{t('clear')}</Clear>
        <Button value='save-filter' title={t('save')} onClick={handleSave} />
      </div>
    </div>
  )
}

export const PopupFilter: FC<Props> = ({ title, icon, ...props }) => {
  const renderTrigger = useCallback(
    (open: boolean) => (
      <ActiveButton title={title} img={icon} className='mb-2 mr-4' active={open} />
    ),
    [title, icon]
  )
  const renderContent = useCallback(
    (close: ChildrenProps['close']) => <Content close={close} {...props} title={title} />,
    [props, title]
  )

  return <Popup trigger={renderTrigger}>{renderContent}</Popup>
}
